/**
 * Everything that does not fit five tabs.
 *
 * A plain list, deliberately: seven destinations of unequal importance are a
 * list, and dressing them as a grid of cards would imply a parity they do not
 * have. Settings is not Backtest's peer.
 *
 * ## Three of these say "not here"
 *
 * Reports, Backtest and Tax export are marked `desktop` and render a sentence
 * instead of a screen. Each is a wide table or a multi-column builder, and a
 * phone version of any of them is worse than an honest redirect — it wastes the
 * trip AND teaches the user the product is broken on their phone.
 *
 * Saying so on the way in is the cheapest possible version of that news. The
 * alternative is a tap, a load, and a table scrolled sideways.
 *
 * @see docs/25-mobile-web-spec.md §2
 */
import type { JSX } from 'react';
import type { IconName } from '@aegis/ui';
import { Icon } from '../components/Icon.js';
import { Link } from '../lib/router.js';

export interface Entry {
  readonly to: string;
  readonly label: string;
  readonly icon: IconName;
  readonly note: string;
  /** True when the screen needs a width a phone does not have. */
  readonly desktop?: boolean;
}

/**
 * Exported so a test can hold it against the tab bar's `owns` list.
 *
 * These two have to agree in both directions: a destination listed here that
 * the More tab does not own leaves the tab bar with nothing selected, and a
 * route the tab owns with no entry here is unreachable on a phone. Both were
 * true of `/fundamentals` until a screenshot showed the tab bar unlit.
 */
export const MORE_ENTRIES: readonly Entry[] = [
  // First, and phone-capable. The desktop rail puts Fundamentals directly under
  // Chart; a phone has five tabs and no room for a sixth, so this list is the
  // only way to reach it — and a destination with no route to it is a
  // destination that does not exist.
  { to: '/fundamentals', label: 'Fundamentals', icon: 'globe', note: 'Economic calendar, risk appetite and headlines' },
  { to: '/analytics', label: 'Analytics', icon: 'analytics', note: 'Distributions, streaks and edge by hour' },
  { to: '/strategies', label: 'Strategies', icon: 'target', note: 'Each with the invalidation that would prove it wrong' },
  { to: '/daily', label: 'Daily', icon: 'calendar', note: 'Prep, review and the checklist that feeds your score' },
  { to: '/agents', label: 'Agents', icon: 'ai', note: 'Session review, game plan, auto-tagger, weekly digest' },
  { to: '/settings', label: 'Settings', icon: 'settings', note: 'Account, broker, devices, preferences' },

  { to: '/reports', label: 'Reports', icon: 'table', note: 'Every measure over every dimension', desktop: true },
  { to: '/backtest', label: 'Backtest', icon: 'replay', note: 'Scored by the same arithmetic as your journal', desktop: true },
  { to: '/tax', label: 'Tax export', icon: 'receipt', note: 'Realised gains, per account, per year', desktop: true },
];

export function MoreScreen(): JSX.Element {
  const here = MORE_ENTRIES.filter((e) => !e.desktop);
  const there = MORE_ENTRIES.filter((e) => e.desktop);

  return (
    <div className="more-screen">
      <ul className="more-list">
        {here.map((e) => (
          <li key={e.to}>
            <Link to={e.to} className="more-row">
              <span className="more-ico"><Icon name={e.icon} /></span>
              <span className="more-text">
                <span className="more-label">{e.label}</span>
                <em className="more-note">{e.note}</em>
              </span>
              <Icon name="chevronRight" size="sm" />
            </Link>
          </li>
        ))}
      </ul>

      <h3 className="panel-h">BETTER ON A LARGER SCREEN</h3>
      <ul className="more-list">
        {there.map((e) => (
          <li key={e.to}>
            {/* Not a link. A disabled-looking row that navigates anyway is the
                worst of both — it either lies about being unavailable or lies
                about being ready. */}
            <div className="more-row is-desktop" aria-disabled="true">
              <span className="more-ico"><Icon name={e.icon} /></span>
              <span className="more-text">
                <span className="more-label">{e.label}</span>
                <em className="more-note">{e.note}</em>
              </span>
            </div>
          </li>
        ))}
      </ul>
      <p className="panel-body">
        These are wide tables. They exist on the desktop and web app at a size where
        the whole row fits, which is the only size at which they are worth reading.
      </p>
    </div>
  );
}
